import React, { Component, ReactElement } from 'react';

import { FileUploaderAPI } from '@philips-emr/tws-components-api';
import { Expression } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { arrayOf, bool, eventListener, expression, number, shape, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { FileService, MessageService } from '@philips-emr/tws-core/lib/services';
import { FileInterface } from '@philips-emr/tws-core/lib/services/file/file-handler-configuration';
import { isFunction } from '@philips-emr/tws-utils';

import { Button, File as FileComponent } from '..';
import FileUploaderStatus from '../file/file-uploader-status';

import './file-uploader.scss';

export interface FileItem {
  file: File;
  onCancel?: () => void;
  path?: string;
  progress?: number;
  percentage?: number;
  state?: FileUploaderStatus;
  status?: FileUploaderStatus;
}

interface FileUploaderState {
  files: Map<string, FileItem>;
}

export interface FileProps {
  filename: string;
  filepath: string;
  size: number;
}

export interface FileUploaderProps {
  cancelUploadExpression: string;
  enabled: boolean;
  failedUploadExpression: string;
  multiple: boolean;
  onAbort: (event: FileItem) => void;
  onChange: (event: Record<string, any[]>) => void;
  onCompleted: (event: FileItem) => void;
  onError: (event: FileItem) => void;
  onPreview: (filePath: Record<string, string>) => void;
  onRemove: (newFile: Record<string, string | number>) => void;
  onRetry: (event: FileItem) => void;
  onUpload: (event: FileItem) => void;
  previewFileExpression: string;
  readOnly: boolean;
  retryUploadExpression: string;
  selectFileExpression: string;
  valid: boolean;
  value: FileProps[];
}

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: FileUploaderAPI })
class FileUploader extends Component<FileUploaderProps, FileUploaderState> {
  state: FileUploaderState = {
    files: new Map(),
  };

  private inputRef = React.createRef<HTMLInputElement>();

  static propTypes = {
    cancelUploadExpression: expression,
    enabled: bool,
    failedUploadExpression: expression,
    multiple: bool,
    onAbort: eventListener,
    onChange: eventListener,
    onCompleted: eventListener,
    onError: eventListener,
    onPreview: eventListener,
    onRemove: eventListener,
    onRetry: eventListener,
    onUpload: eventListener,
    previewFileExpression: expression,
    readOnly: bool,
    retryUploadExpression: expression,
    selectFileExpression: expression,
    valid: bool,
    value: arrayOf(shape({ filename: string, filepath: string, size: number })),
  };

  static defaultProps = {
    cancelUploadExpression: Expression.CANCEL_FILE_UPLOADER,
    enabled: true,
    failedUploadExpression: Expression.FAILED,
    multiple: true,
    previewFileExpression: Expression.PREVIEW,
    retryUploadExpression: Expression.RETRY,
    selectFileExpression: Expression.SELECT_FILE,
    valid: true,
    value: [],
  };

  canIncludeFile = (): boolean => {
    const { enabled, multiple, readOnly, value } = this.props;
    const { files } = this.state;

    if (!enabled || readOnly) {
      return false;
    }

    return multiple ? true : (files.size + value.length) < 1;
  };

  onClick = (): void => {
    if (!this.inputRef.current) {
      return;
    }

    this.inputRef.current.click();
  };

  handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = event.target.files;

    if(!files) {
      return;
    }

    Array.from(files).forEach(file => this.uploadFile(file));
  };

  uploadFile(file: File): Map<string, FileItem> | void {
    const { onUpload } = this.props;

    if (this.findFile(file.name) || !this.canIncludeFile()) {
      return;
    }

    if(this.inputRef.current) {
      this.inputRef.current.files = null;
      this.inputRef.current.value = '';
    }

    const item: FileItem = {
      file,
    };

    this.setState(({ files }) => {
      return {
        // Map.set returns the map.
        files: files.set(file.name, item),
      };
    }, () => {
      FileService.upload(file, { onProgress: this.onProgress })
        .then(this.onProgress)
        .catch(this.onProgress);
    });

    if (isFunction(onUpload)) {
      onUpload({ file });
    }
  }

  onRetry = (fileName: string): void => {
    const { onRetry } = this.props;

    const changes = {
      percentage: 0,
      state: FileUploaderStatus.PENDING,
    };

    const updateCallback = (files: Map<string, FileItem>): void => {
      const item = files.get(fileName);

      if(!item) {
        return;
      }

      FileService.upload(item.file, { onProgress: this.onProgress })
        .then(this.onProgress)
        .catch(this.onProgress);

      this.setState({ files });

      if (isFunction(onRetry)) {
        onRetry({
          file: item.file,
          progress: item.percentage,
          status: item.state,
        });
      }
    };

    this.updateFileItem(fileName, changes, updateCallback);
  };

  findFile(fileName: string): boolean {
    const { files } = this.state;

    return files.has(fileName);
  }

  updateFileItem(fileName: string, changes: Record<string, any>, callback: Function): void {
    const { enabled, onAbort, onCompleted, onError, readOnly } = this.props;
    const { files } = this.state;

    if (readOnly || !enabled) {
      return;
    }

    const item = files.get(fileName);

    if (!item) {
      return;
    }

    const { file } = item;

    const updatedFiles = files.set(fileName, { file, ...changes });

    const updatedItem = updatedFiles.get(fileName);

    if (!updatedItem) {
      return;
    }

    const { path, percentage, state } = updatedItem;

    if (state === FileUploaderStatus.ERROR && isFunction(onError)) {
      onError({ file, path, progress: percentage, status: state });
    }

    if (state === FileUploaderStatus.DONE && isFunction(onCompleted)) {
      onCompleted({ file, path, progress: percentage, status: state });
    }

    if (state === FileUploaderStatus.ABORTED && isFunction(onAbort)) {
      onAbort({ file, path, progress: percentage, status: state });
    }

    if (state === FileUploaderStatus.ABORTED) {
      updatedFiles.delete(fileName);
    }

    callback(updatedFiles);
  }

  onProgress = (response: FileInterface): void => {
    const { fileName, path, percentage, state } = response.uploadProgress;

    const changes = {
      onCancel: response.onCancel,
      path,
      percentage,
      state,
    };

    const updateCallback = (files: Map<string, FileItem>): void => this.setState({ files }, this.triggerOnChange);

    this.updateFileItem(fileName, changes, updateCallback);
  };

  triggerOnChange(): void {
    const { onChange, value } = this.props;
    const { files } = this.state;

    if (!isFunction(onChange)) {
      return;
    }

    const processed = [];
    const processing = new Map();

    for (const [key, value] of files) {
      if (value.state === FileUploaderStatus.DONE) {
        const item = {
          filename: value.file.name,
          filepath: value.path,
          size: value.file.size ?? 0,
        };

        processed.push(item);
      } else {
        processing.set(key, value);
      }
    }

    this.setState({ files: processing });
    onChange({ value: [...value, ...processed], oldValue: value });
  }

  onRemove = (name: string, path: string): void => {
    FileService.remove(path, name, this.onExecuteRemove);
  };

  onExecuteRemove = (name: string, path: string, status: number, removed: boolean): void => {
    const { onChange, onRemove, value: oldValue } = this.props;

    if (!isFunction(onChange)) {
      return;
    }

    if (removed) {
      const value = oldValue.filter((item) => item.filename !== name);

      onChange({ oldValue, value });
    }

    if (isFunction(onRemove)) {
      onRemove({ path, status });
    }
  };

  onPreview = (path: string): void => {
    const { onPreview } = this.props;

    FileService.preview(path);

    if(isFunction(onPreview)) {
      onPreview({ path });
    }
  };

  onCancel = ({ onCancel }: FileItem ): void => {
    if (isFunction(onCancel)) {
      onCancel();
    }
  };

  getFiles(): FileItem[] {
    const { value } = this.props;
    const { files } = this.state;

    // @ts-ignore
    const mappedValues: FileItem[] = value.map((item: FileProps) => {
      return {
        file: {
          name: item.filename,
          size: item.size ?? 0,
        },
        path: item.filepath,
        state: FileUploaderStatus.DONE,
      };
    });

    return [...mappedValues].concat(Array.from(files.values()));
  }

  renderInputButton(): ReactElement | null {
    const { multiple, readOnly, selectFileExpression } = this.props;
    const canChooseFile = this.canIncludeFile();

    if (readOnly || !canChooseFile) {
      return null;
    }

    return (
      <React.Fragment>
        <input
          className="c-file-uploader__input"
          disabled={!canChooseFile}
          multiple={multiple ? true : undefined}
          onChange={this.handleChange}
          ref={this.inputRef}
          type="file"
        />
        <Button
          className="c-file-uploader__select-file c-button c-button--outline"
          enabled={canChooseFile}
          onClick={this.onClick}
          value={MessageService.msg(selectFileExpression)}
        />
      </React.Fragment>
    );
  }

  render(): ReactElement {
    const {
      cancelUploadExpression,
      failedUploadExpression,
      previewFileExpression,
      readOnly,
      retryUploadExpression,
    } = this.props;

    return (
      <div className="c-file-uploader">
        {this.renderInputButton()}
        <ul>
          {this.getFiles().map((item: FileItem, index) => (
            <FileComponent
              cancelUploadExpression={cancelUploadExpression}
              failedUploadExpression={failedUploadExpression}
              key={index}
              onCancel={() => this.onCancel(item)}
              onPreview={() => this.onPreview(item.path ?? '/')}
              onRemove={() => this.onRemove(item.file.name, item.path ?? '/')}
              onRetry={() => this.onRetry(item.file.name)}
              previewFileExpression={previewFileExpression}
              progress={item.percentage}
              readOnly={readOnly}
              retryUploadExpression={retryUploadExpression}
              size={item.file.size}
              status={item.state}
              title={item.file.name}
            />
          ))}
        </ul>
      </div>
    );
  }
}

export default FileUploader;
